import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import buildCalendarDays from './helpers/buildCalendarDays'
import moment from 'moment'

// Assets
import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from 'assets/images/Icons'

// Constants
import { DASHED_DATE_FORMAT } from 'common/constants/dateFormat'

// Utils
import { useOutsideAlerter } from 'common/utils/outsideAlerter'

// Components
import Button from '../../Buttons/Button'

// Styled Components
import {
  DatePickerWrapper,
  DatePickerWrapperHeader,
  DatePickerDate,
  Label,

  // Picker
  DatePickerContainer,
  DatePickerHeader,
  DateChanger,
  DateChangerWrapper,
  DateChangerContainer,
  DateContentWrapper,
  DateGroupWrapper,
  DateGroupTitle,
  DateWeekWrapper,
  DateDayWrapper,
  DateDayContainer,
} from './FutureDatePicker.elements'

function FutureDatePicker(props) {
  // props
  const { onApply, disabled, placeholder, defaultDate, maxDate, label, ...rest } = props

  const [open, setOpen] = useState(false)
  const [days, setDays] = useState([])
  const [currDay, setCurrDay] = useState(moment())
  const [selectedDate, setSelectedDate] = useState(defaultDate ? moment(defaultDate) : null)

  const pickerRef = useRef()

  // functions
  const handleOpen = () => {
    if (!disabled) setOpen((prevValue) => !prevValue)
  }
  const handleCancel = () => {
    if (selectedDate) {
      setCurrDay(selectedDate)
      setOpen(false)
    } else {
      setOpen(false)
    }
  }
  const moveBack = () => {
    if (!currDay.isBefore(moment().subtract(1, 'month'), 'month')) {
      setCurrDay(currDay.clone().subtract(1, 'month'))
    }
  }
  const moveNext = () => {
    setCurrDay(currDay.clone().add(1, 'month'))
  }
  const getYears = (forward) => {
    const year = new Date().getFullYear()
    let reversedYears = []
    Array.from({ length: forward }, (v, i) => {
      reversedYears = [...reversedYears, { value: year + i + 1, label: year + i + 1 }]
    })
    return reversedYears
  }

  const handleMonthChange = (value) => {
    setCurrDay((prevValue) => prevValue.clone().set('month', value))
  }
  const handleYearChange = (value) => {
    setCurrDay((prevValue) => prevValue.clone().set('year', value))
  }
  const handleClick = (value) => {
    setSelectedDate(value)
    onApply(value.format(DASHED_DATE_FORMAT))
    setOpen(false)
  }
  // UseEffect
  useEffect(() => {
    setDays(buildCalendarDays(currDay))
  }, [currDay])
  useOutsideAlerter(pickerRef, handleCancel)
  useEffect(() => {
    if (defaultDate) setSelectedDate(moment(defaultDate))
  }, [defaultDate])

  return (
    <DatePickerWrapper {...rest} ref={pickerRef}>
      {label && <Label>{label}</Label>}
      <DatePickerWrapperHeader onClick={handleOpen} disabled={disabled}>
        <DatePickerDate>
          {selectedDate ? selectedDate.format('MMMM DD YYYY') : placeholder || 'Select Date'}
        </DatePickerDate>
      </DatePickerWrapperHeader>

      <DatePickerContainer open={open}>
        <DatePickerHeader>
          <Button
            onClick={moveBack}
            disabled={currDay.isBefore(moment().subtract(1, 'month'), 'month')}
            variant="outlined"
            type="button"
            startIcon={<ArrowLeftCircleIcon size={18} />}
          />
          <DateChangerWrapper>
            <DateChangerContainer>
              <DateChanger
                options={moment.months().map((item) => ({ value: item, label: item }))}
                onChange={handleMonthChange}
                value={currDay.format('MMMM')}
                placeholder={currDay.format('MMMM')}
              />
            </DateChangerContainer>
            <DateChangerContainer>
              <DateChanger
                options={getYears(100)}
                onChange={handleYearChange}
                value={currDay.format('YYYY')}
                placeholder={currDay.format('YYYY')}
              />
            </DateChangerContainer>
          </DateChangerWrapper>
          <Button onClick={moveNext} variant="outlined" startIcon={<ArrowRightCircleIcon size={18} />} type="button" />
        </DatePickerHeader>

        <DateContentWrapper>
          <DateGroupWrapper>
            <DateGroupTitle>{currDay.format('MMMM YYYY')}</DateGroupTitle>
            <DateWeekWrapper>
              {days.map((week) => (
                <DateDayWrapper key={week} style={{ display: 'flex', gap: '20px' }}>
                  {week.map((day) => (
                    <DateDayContainer
                      key={day.format('MM/DD')}
                      onClick={() =>
                        !day.isBefore(moment(), 'day') &&
                        (maxDate ? day.isBefore(moment(maxDate), 'day') : true) &&
                        handleClick(day)
                      }
                      forbidden={day.isBefore(moment(), 'day') || (maxDate && day.isAfter(moment(maxDate), 'day'))}
                      disabled={
                        day.isBefore(moment(), 'day') ||
                        !day.isSame(currDay, 'month') ||
                        (maxDate && day.isAfter(moment(maxDate), 'day'))
                      }
                      active={day.isSame(selectedDate)}
                    >
                      {day.format('D')}
                    </DateDayContainer>
                  ))}
                </DateDayWrapper>
              ))}
            </DateWeekWrapper>
          </DateGroupWrapper>
        </DateContentWrapper>
      </DatePickerContainer>
    </DatePickerWrapper>
  )
}

// Default Props
FutureDatePicker.defaultProps = {
  onApply: () => {},
  defaultDate: '',
  maxDate: '',
  simplifiedDate: false,
  disabled: false,
  placeholder: '',
}

// Proptypes Validation
FutureDatePicker.propTypes = {
  onApply: PropTypes.func,
  defaultDate: PropTypes.string,
  maxDate: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  simplifiedDate: PropTypes.bool,
  disabled: PropTypes.bool,
}

export default FutureDatePicker
